<template>
    <v-dialog v-model="isOpen" width="1024">
        <v-card>
            <v-toolbar color="primary" dark flat>
                <v-toolbar-title>
                    {{ mode === 'create' ? 'Benachrichtigung erstellen' : 'Benachrichtigung' }}
                </v-toolbar-title>
                <v-spacer/>
                <v-btn plain @click="bulkEdit = !bulkEdit">
                    <v-icon v-show="!bulkEdit">mdi-application-brackets-outline</v-icon>
                    <v-icon v-show="bulkEdit">mdi-application-edit-outline</v-icon>
                </v-btn>
            </v-toolbar>
            <template v-if="bulkEdit">
                <campaign-json-editor :value="campaign"
                                      :readonly="mode==='show'"
                                      @input="newCampaign = $event"/>
            </template>
            <template v-else>
                <v-container v-if="campaignsLoaded && campaign">

                    <v-alert :value="showBisecurWarning"
                             transition="slide-x-transition"
                             color="warning">
                        BiSecur App kann keine Push Benachrichtigungen empfangen
                    </v-alert>
                    <v-alert :value="this.errors.length > 0"
                             transition="slide-x-transition"
                             color="error" dark>
                        <ul>
                            <li v-for="(error, index) in errors" :key="index">
                                {{ error.text }}
                            </li>
                        </ul>
                    </v-alert>

                    <v-row v-if="mode === 'show'">
                        <v-col>
                            <v-alert v-if="campaign.type === 'queued_job'"
                                     color="warning"
                            >
                                Status: Veröffentlichung geplant für {{ schedule }}
                            </v-alert>
                            <v-alert v-if="campaign.type === 'campaign'"
                                     color="success"
                            >
                                Status: Veröffentlicht
                            </v-alert>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-card outlined :class="{'error-border': elementHasError('NotificationApps')}">
                                <notification-apps :selectedApps="campaign.apps"
                                                   @change="updateProperty('apps', $event)"
                                                   :readonly="mode === 'show'"
                                                   :color="elementHasError('NotificationApps')?'error':'primary'"/>
                            </v-card>
                            <v-card outlined
                                    :class="{'error-border': elementHasError('NotificationChannels'), 'my-5':true}">
                                <notification-channels :campaign="campaign"
                                                       :type="notificationType"
                                                       :type-error="elementHasError('NotificationType')"
                                                       @change="updateChannels($event)"
                                                       @change:type="updateNotificationType($event)"
                                                       :readonly="mode === 'show'"
                                                       :color="elementHasError('NotificationChannels')?'error':'primary'"/>
                            </v-card>
                            <v-card v-if="mode === 'create'"
                                    outlined>
                                <notification-schedule @change="updateProperty('schedule', $event)"/>
                            </v-card>
                        </v-col>
                        <v-col>
                            <v-card class="error-border"
                                    :outlined="elementHasError('Notifications')"
                                    :flat="!elementHasError('Notifications')">
                                <v-expansion-panels v-model="openedPanel">
                                    <notification-expansion-panel language="de"
                                                                  :value="getNotification(campaign, 'de')"
                                                                  @change="updateNotification('de', $event)"
                                                                  :readonly="mode === 'show'"
                                                                  :errors="errorsForElement('Notification_de')"/>
                                    <notification-expansion-panel language="en"
                                                                  :value="getNotification(campaign, 'en')"
                                                                  @change="updateNotification('en', $event)"
                                                                  :readonly="mode === 'show'"
                                                                  :errors="errorsForElement('Notification_en')"/>
                                </v-expansion-panels>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container v-else>
                    <v-row justify="center">
                        <v-col cols="auto">
                            <v-progress-circular color="primary" size="112" indeterminate/>
                        </v-col>
                    </v-row>
                </v-container>

            </template>

            <v-card-actions v-if="mode === 'create'">
                <v-spacer/>
                <v-btn text @click="close">Abbrechen</v-btn>

                <confirm-button color="primary"
                                title="Wirklich veröffentlichen"
                                confirm-text="Veröffentlichen"
                                cancel-text="abbrechen"
                                confirm-first width="180"
                                nudge-left="190" nudge-bottom="1"
                                @confirm="submit">
                    Veröffentlichen
                </confirm-button>

            </v-card-actions>
            <v-card-actions v-if="mode === 'show'">
                <v-spacer/>
                <v-btn text @click="close">Schließen</v-btn>
                <confirm-button color="error"
                                title="Wirklich löschen?"
                                confirm-text="Löschen"
                                cancel-text="abbrechen"
                                confirm-first
                                nudge-left="135" nudge-bottom="1"
                                @confirm="destroy">
                    Löschen
                </confirm-button>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import NotificationExpansionPanel from "./campaignDialog/NotificationExpansionPanel";
import NotificationChannels from "./campaignDialog/NotificationChannels";
import NotificationApps from "./campaignDialog/NotificationApps";
import {mapActions, mapGetters} from "vuex";
import ConfirmButton from "../ConfirmButton";
import CampaignJsonEditor from "./campaignDialog/CampaignJsonEditor";
import NotificationSchedule from "./campaignDialog/NotificationSchedule";
import moment from "moment";

export default {
    name: "CampaignDialog",
    components: {
        NotificationSchedule,
        CampaignJsonEditor,
        ConfirmButton,
        NotificationApps,
        NotificationChannels,
        NotificationExpansionPanel
    },
    data() {
        return {
            bulkEdit: false,
            errors: [],
            openedPanel: 0,
            newCampaign: {
                channel_api: false,
                channel_apn: false,
                channel_android: false,
                notifications: []
            },
            notificationType: null,
        };
    },
    computed: {
        ...mapGetters('campaign', [
            'campaignsLoaded',
            'getCampaignById',
            'getNotification'
        ]),
        isOpen: {
            get() {
                return true;
            },
            set(value) {
                if (!value) {
                    this.close();
                }
            },
        },
        campaign: {
            get() {
                if (this.mode === 'show') {
                    console.log('show campaign', this.getCampaignById(this.$route.params.id, this.$route.params.type))
                    return this.getCampaignById(this.$route.params.id, this.$route.params.type);
                } else {
                    return this.newCampaign;
                }
            },
        },
        readonly() {
            return this.$route.params['id'] !== undefined;
        },
        mode() {
            return this.$route.params.id === undefined ? 'create' : 'show';
        },
        showBisecurWarning() {
            return (this.campaign.apps?.find(app => app.id === 'bisecur') !== undefined
                && (this.campaign.channel_apn || this.campaign.channel_android));
        },
        schedule() {
            if (this.campaign.type !== 'queued_job') {
                return "";
            }
            return moment(this.campaign.schedule).format('DD.MM.YYYY HH:mm')
        }
    },
    methods: {
        ...mapActions('campaign', [
            'createCampaign',
            'deleteCampaign'
        ]),
        elementHasError(element) {
            return this.errors.find(error => error.element === element) !== undefined;
        },
        errorsForElement(element) {
            return this.errors.filter(error => error.element === element);
        },
        updateChannels(value) {
            ['channel_api', 'channel_apn', 'channel_android'].forEach(channel => {
                this.newCampaign[channel] = value.includes(channel);
            });
            this.errors = this.errors.filter(error => error.element !== 'NotificationChannels');
        },
        updateNotificationType(value) {
            this.errors = this.errors.filter(error => error.element !== 'NotificationType');

            this.notificationType = value;
            this.newCampaign.notifications = this.newCampaign.notifications.map(notification => {
                return Object.assign(notification, {type: value ? value : 'info'});
            });
        },
        updateProperty(property, value) {

            if (property === 'apps') {
                this.errors = this.errors.filter(error => error.element !== 'NotificationApps');
            }

            this.newCampaign = Object.assign({}, this.newCampaign, {
                [property]: value
            });
        },
        updateNotification(language, value) {
            this.errors = this.errors.filter(error =>
                error.element !== 'Notifications'
                && error.element !== 'Notification_de'
                && error.element !== 'Notification_en'
            );

            if (!this.newCampaign.notifications.find(notification => notification.language === language)) {
                this.newCampaign.notifications.push({language})
            }
            this.newCampaign.notifications = this.newCampaign.notifications.map(notification => {
                if (notification.language === language) {
                    notification = Object.assign(notification, value);
                }
                return notification;
            }).filter(notification => notification.isActive);
            this.updateNotificationType(this.notificationType);
        },
        validate() {
            this.errors = [];
            return new Promise((resolve, reject) => {
                if (!this.newCampaign.apps || this.campaign.apps.length < 1) {
                    this.errors.push({
                        element: 'NotificationApps',
                        text: 'Es muss mindestens eine App ausgewählt werden'
                    });
                }
                if (!this.newCampaign.channel_api
                    && !this.newCampaign.channel_apn
                    && !this.newCampaign.channel_android) {
                    this.errors.push({
                        element: 'NotificationChannels',
                        text: 'Es muss mindestens eine Benachrichtigungsoption gewählt werden'
                    });
                }
                if (!this.newCampaign.notifications || this.newCampaign.notifications.length < 1) {
                    this.errors.push({
                        element: 'Notifications',
                        text: 'Es muss mindestens eine Spreche Aktiv sein'
                    });
                }
                if (this.newCampaign.channel_api && !this.notificationType) {
                    this.errors.push({
                        element: 'NotificationType',
                        text: 'In App Benachrichtigungen benötigen ein Icon'
                    })
                }

                let activeNotifications = this.newCampaign.notifications.filter(notification => notification.isActive)
                activeNotifications.forEach(n => {
                    if (!n.title || n.title === '') {
                        this.errors.push({
                            element: 'Notification_' + n.language,
                            subElement: 'Title',
                            text: 'Der Titel muss für jede aktive Sprache gefüllt sein'
                        });
                    }
                    if (!n.content || n.content === '') {
                        this.errors.push({
                            element: 'Notification_' + n.language,
                            subElement: 'Content',
                            text: 'Die Nachricht muss für jede aktive Sprache gefüllt sein'
                        });
                    }
                });

                if (this.errors.length > 0) {
                    reject();
                } else {
                    resolve();
                }
            });
        },
        submit() {
            this.validate()
                .then(_ => this.createCampaign(this.newCampaign)
                    .then(campaign => {
                        this.$router.replace({name: 'ShowCampaign', params: {id: campaign.id, type: campaign.type}});
                    }));
        },
        destroy() {

            this.deleteCampaign(this.campaign)
                .then(_ => {
                    this.close();
                });
        },
        close() {
            let split = this.$route.path.split('/');
            split.pop();
            split.pop();
            this.$router.replace('/' + split.join('/'));
        }
    },
}
</script>

<style scoped>
.error-border {
    border-color: #e9605c
}
</style>
